import { selectIsBossOrSalesBossLevel } from 'core/userContext';
import { fetchIntegrations, fetchMobileApps } from 'modules/mobileApps/actions';
import {
  IntegrationMetadataProvider,
  useIntegrationMetadataContext,
} from 'modules/mobileApps/context/integrationMetadata';
import { ModalProvider } from 'modules/mobileApps/context/modal';
import {
  getIsFetchingMobileApps,
  getUnassignedIntegrations,
  selectMobileApps,
} from 'modules/mobileApps/selectors';
import { FC, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BUTTON_SIZES } from 'ui/button';
import { Add16 } from 'ui/icons';
import { LinkAddButton } from 'ui/linkButton';
import { PageHeader } from 'ui/pageHeader';
import { EmptyState, UnassignedIntegrations } from '..';
import { Modals } from '../modals';
import { AppList } from './AppList';
import { IntergrationsContainer } from './styles';

const withContext = (Component: FC) => () => (
  <IntegrationMetadataProvider>
    <ModalProvider>
      <Component />
    </ModalProvider>
  </IntegrationMetadataProvider>
);

export const AppsAndWebsites = withContext(() => {
  const isBaus = useSelector(selectIsBossOrSalesBossLevel);

  const dispatch = useDispatch();

  const apps = useSelector(selectMobileApps);
  const unassignedIntegrations = useSelector(getUnassignedIntegrations);
  const isFetchingApps = useSelector(getIsFetchingMobileApps);

  const { setApps } = useIntegrationMetadataContext();

  useEffect(() => {
    if (apps?.length) setApps(apps);
  }, [apps]);

  useEffect(() => {
    dispatch(fetchMobileApps());
    dispatch(fetchIntegrations());
  }, []);

  const hasNoApps = !isFetchingApps && apps.length === 0;
  const hasUnassignedIntegrations = !!unassignedIntegrations?.length;
  const showEmptyState = isFetchingApps || hasNoApps;

  return (
    <>
      <PageHeader
        data-test="mobile-apps-page-header"
        pageActions={
          <LinkAddButton to="/integrations/mobileApps/create" size={BUTTON_SIZES.LARGE}>
            Add new
          </LinkAddButton>
        }
      >
        Apps & websites
      </PageHeader>
      {showEmptyState ? (
        <EmptyState isLoading={isFetchingApps}>
          <Add16 height={32} width={32} />
          Add a new app or website to get started.
        </EmptyState>
      ) : (
        <AppList />
      )}
      {(hasUnassignedIntegrations || isBaus) && (
        <IntergrationsContainer>
          <UnassignedIntegrations integrations={unassignedIntegrations} />
        </IntergrationsContainer>
      )}
      <Modals />
    </>
  );
});
